import { useMemo } from 'react'
import { useAgenciesList } from '~/api/queries/agency'
import { UsersChipValue } from '~/components/Filters/FilterBar/chips/users'
import type { FilterDefinition } from '~/components/Filters/FilterBar/types'
import { ProximityFilterEditor } from '~/domains/companies/components/ProximityFilterEditor'
import { parseProximity } from '~/domains/companies/proximity'
import { contactTypes } from '~/domains/contacts/contactTypes'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { UsersAutocomplete } from '~/domains/users/components/UsersAutocomplete'

export const useCompaniesFilterDefinitions = (): FilterDefinition[] => {
  const { data: agencies } = useAgenciesList('contacts')
  const { data: companyStatuses } = useTaxonomy('company_statuses')
  const { data: sectors } = useTaxonomy('sectors')
  const { data: potentials } = useTaxonomy('potentials')
  const { data: segmentations } = useTaxonomy('segmentations')
  const { data: qualifications } = useTaxonomy('qualifications')

  return useMemo(
    () => [
      {
        key: 'name',
        label: 'Raison sociale',
        type: 'text',
        placeholder: 'Rechercher une raison sociale'
      },
      {
        key: 'registrationNumber',
        label: 'SIRET',
        type: 'text',
        placeholder: 'Rechercher un SIRET'
      },
      {
        key: 'status',
        label: 'Statut',
        type: 'select',
        multiSelect: true,
        options:
          companyStatuses?.map((status) => ({
            label: status.name,
            value: String(status.id)
          })) ?? [],
        emptyLabel: 'Tous'
      },
      {
        key: 'archived',
        label: 'Entreprises archivées',
        type: 'select',
        options: [
          { value: 'true', label: 'Uniquement' },
          { value: 'false', label: 'Exclues' }
        ],
        emptyLabel: 'Incluses'
      },
      {
        key: 'potential',
        label: 'Potentiel',
        type: 'select',
        multiSelect: true,
        options:
          potentials?.map((potential) => ({
            label: potential.name,
            value: String(potential.id)
          })) ?? [],
        emptyLabel: 'Tous'
      },
      {
        key: 'contactTypes',
        label: 'Types de contact',
        type: 'select',
        multiSelect: true,
        options: contactTypes.map(({ key, label }) => ({ value: key, label })),
        emptyLabel: 'Tous'
      },
      {
        label: 'Dernière action',
        menuLabel: 'Date de dernière action',
        type: 'dateRange',
        keyAfter: 'lastActionAfter',
        keyBefore: 'lastActionBefore'
      },
      {
        key: 'qualifications',
        label: 'Qualifications de recours',
        type: 'select',
        multiSelect: true,
        searchable: true,
        options:
          qualifications?.map((qualification) => ({
            label: qualification.name,
            value: String(qualification.id)
          })) ?? [],
        emptyLabel: 'Toutes'
      },
      {
        key: 'near',
        label: 'À proximité',
        type: 'autocomplete',
        renderEditor: ({ value, onChange, onClose }) => (
          <ProximityFilterEditor
            value={value}
            onChange={onChange}
            onClose={onClose}
          />
        ),
        renderValue: (value) => {
          const proximity = parseProximity(value)
          return proximity
            ? `${proximity.label} (${proximity.radius} km)`
            : value
        }
      },
      {
        key: 'segmentations',
        label: 'Segments',
        type: 'select',
        multiSelect: true,
        searchable: true,
        options:
          segmentations?.map((segmentation) => ({
            label: segmentation.name,
            value: String(segmentation.id)
          })) ?? [],
        emptyLabel: 'Tous'
      },
      {
        key: 'sector',
        label: 'Secteur',
        type: 'select',
        multiSelect: true,
        searchable: true,
        options:
          sectors?.map((sector) => ({
            label: sector.name,
            value: String(sector.id)
          })) ?? [],
        emptyLabel: 'Tous'
      },
      {
        key: 'linkedUsers',
        label: 'Utilisateurs',
        type: 'autocomplete',
        renderEditor: ({ value, onChange }) => (
          <UsersAutocomplete
            key={value || 'empty'}
            inlineList
            multiple
            ids={
              value
                ? value
                    .split(',')
                    .map((entry) => entry.trim())
                    .filter(Boolean)
                : []
            }
            listingContext="contacts"
            small
            fullWidth
            placeholder="Rechercher des utilisateurs"
            emptyLabel="Tous"
            onChange={(nextValues) => {
              if (nextValues.includes('')) {
                onChange('')
                return
              }
              onChange(nextValues.filter(Boolean).join(','))
            }}
          />
        ),
        renderValue: (value) => <UsersChipValue value={value} />
      },
      {
        key: 'agency',
        label: 'Agences',
        type: 'select',
        multiSelect: true,
        searchable: true,
        options:
          agencies?.map((agency) => ({
            label: agency.name,
            value: String(agency.id)
          })) ?? [],
        emptyLabel: 'Toutes'
      }
    ],
    [
      agencies,
      companyStatuses,
      sectors,
      potentials,
      segmentations,
      qualifications
    ]
  )
}
